import "./PollyQuestionTypeMenu.scss";

import {
  Flex,
  Menu,
  MenuItemProps,
  ShorthandCollection,
  Text,
  Tooltip,
} from "@fluentui/react-northstar";
import { QuestionTemplate } from "@teams/types";
import { QuestionTemplateDTO } from "@teams/types/api";
import React, { useMemo } from "react";

import { PollyIcon, PollyIconName } from "../../../_shared/components/PollyIcon";
import { QuestionTypeSelectionMenuTypes } from "./PollySurveyAuthoringForm";

export const PollyQuestionTypeMenu: React.FC<{
  activeType: QuestionTypeSelectionMenuTypes;
  activeTemplate?: string;
  templates: QuestionTemplateDTO[];
  allowedTypes: QuestionTypeSelectionMenuTypes[];
  compressed?: boolean;
  onTypeSelected: (type: QuestionTypeSelectionMenuTypes, templateId?: string) => void;
  disableMenu?: boolean;
}> = ({
  activeType,
  activeTemplate,
  onTypeSelected,
  templates,
  allowedTypes,
  compressed,
  disableMenu,
}) => {
  const menuItems = useMemo(() => {
    const items: ShorthandCollection<MenuItemProps> = [];

    const tooltip = getMainMenuToolTip(activeType);
    const icon = getMainMenuIcon(activeType);

    const menuItems: MenuItemProps & { key: string } = {
      key: "question-type",
      icon: compressed ? undefined : (
        <Tooltip content={tooltip}>
          <PollyIcon icon={icon} inheritColor />
        </Tooltip>
      ),
      content: getMainMenuContent(activeTemplate, compressed, templates, activeType),
      menu: (
        <Flex>
          {allowedTypes.some(type => ["nominal", "open-ended"].includes(type)) && (
            <GeneralTypeQuestionMenu
              allowedTypes={allowedTypes}
              compressed={compressed}
              onTypeSelected={onTypeSelected}
            />
          )}
          {allowedTypes.includes("ordinal") && (
            <RatingQuestionMenu templates={templates} onTypeSelected={onTypeSelected} />
          )}
          {allowedTypes.includes("quiz") && (
            <EducationMenu compressed={compressed} onTypeSelected={onTypeSelected} />
          )}
        </Flex>
      ),
      ...(disableMenu && { disabled: true }),
    };

    items.push(menuItems);
    return items;
  }, [
    activeTemplate,
    activeType,
    onTypeSelected,
    templates,
    allowedTypes,
    compressed,
    disableMenu,
  ]);

  return (
    <Flex>
      <Menu className="polly-question-type-menu" primary items={menuItems} />
    </Flex>
  );
};

const RatingQuestionMenu: React.FC<{
  templates: QuestionTemplate[];
  onTypeSelected: (type: QuestionTypeSelectionMenuTypes, templateId?: string) => void;
}> = ({ templates, onTypeSelected }) => (
  <Flex column>
    <Text className="category-label" content="Rating Types" size="medium" />
    <Menu
      className="question-type-sub-menus"
      vertical
      items={templates
        .filter(t => t.type === "ordinal")
        .map(template => ({
          key: template.id,
          content: template.label,
          icon: (
            <Tooltip content="Rating">
              <PollyIcon icon="rating-icon-borderless" inheritColor />
            </Tooltip>
          ),
          onClick: () => onTypeSelected("ordinal", template.id),
        }))}
    />
  </Flex>
);

const EducationMenu: React.FC<{
  compressed?: boolean;
  onTypeSelected: (type: QuestionTypeSelectionMenuTypes, templateId?: string) => void;
}> = ({ compressed, onTypeSelected }) => {
  return (
    <Flex column>
      <Text className="category-label" content="Education" size="medium" />
      <Menu
        className="question-type-sub-menus"
        vertical
        items={[
          {
            key: "quiz",
            icon: (
              <Tooltip content="Quiz">
                <PollyIcon icon="quiz" inheritColor />
              </Tooltip>
            ),
            content: (
              <Flex column>
                {!compressed && (
                  <>
                    <Text content="Quiz" />
                    <Text
                      className="quiz-menu-subtext"
                      size="small"
                      content="Scored questions only"
                    />
                  </>
                )}
              </Flex>
            ),
            onClick: () => onTypeSelected("quiz"),
          },
        ]}
      />
    </Flex>
  );
};

const GeneralTypeQuestionMenu: React.FC<{
  allowedTypes: QuestionTypeSelectionMenuTypes[];
  compressed: boolean | undefined;
  onTypeSelected: (type: QuestionTypeSelectionMenuTypes, templateId?: string) => void;
}> = ({ allowedTypes, compressed, onTypeSelected }) => {
  return (
    <Flex column>
      <Text className="category-label" content="General Types" size="medium" />
      <Menu
        className="question-type-sub-menus"
        vertical
        items={[
          {
            ...(allowedTypes.includes("nominal") && {
              key: "multichoice",
              icon: (
                <Tooltip content="Multiple choice">
                  <PollyIcon icon="multiple-choice-icon" inheritColor />
                </Tooltip>
              ),
              content: compressed ? undefined : "Multiple Choice",
              onClick: () => onTypeSelected("nominal"),
            }),
          },
          {
            ...(allowedTypes.includes("open-ended") && {
              key: "openended",
              icon: (
                <Tooltip content="Open Ended">
                  <PollyIcon icon="survey-question-icon" inheritColor />
                </Tooltip>
              ),
              content: compressed ? undefined : "Open ended",
              onClick: () => onTypeSelected("open-ended"),
            }),
          },
        ]}
      />
    </Flex>
  );
};

function getMainMenuContent(
  activeTemplate: string | undefined,
  compressed: boolean | undefined,
  templates: QuestionTemplate[],
  activeType: string
) {
  return activeTemplate && !compressed
    ? templates.find(t => t.id === activeTemplate)?.label
    : compressed
    ? undefined
    : activeType === "nominal"
    ? "Multiple Choice"
    : activeType === "open-ended"
    ? "Open Ended"
    : activeType === "quiz"
    ? "Quiz"
    : "Rating";
}

function getMainMenuIcon(activeType: string): PollyIconName {
  return activeType === "nominal"
    ? "multiple-choice-icon"
    : activeType === "open-ended"
    ? "survey-question-icon"
    : activeType === "quiz"
    ? "quiz"
    : "rating-icon-borderless";
}

function getMainMenuToolTip(activeType: string) {
  return activeType === "nominal"
    ? "Multiple Choice"
    : activeType === "open-ended"
    ? "Open Ended"
    : activeType === "quiz"
    ? "Quiz"
    : "Rating";
}
